import { useEffect, useState } from 'react';
import { collectionsApi, seerrApi, type ExtItem, type Saga, type SagaPart } from '../api';
import { RequestDialog } from '../components/Widgets';
import { goBack, linkProps, navigate } from '../router';

/** Eén filmreeks: welke delen je hebt, zag en mist, in volgorde van verschijnen. */
export function SagaPage({ id }: { id: number }) {
  const [saga, setSaga] = useState<Saga | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<ExtItem | null>(null);
  const [bulk, setBulk] = useState<string | null>(null);

  const load = () => collectionsApi.get(id).then((r) => setSaga(r.saga)).catch(() => setError('Deze reeks kon niet worden geladen.'));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return <main className="page"><p className="placeholder-row">{error}</p></main>;
  if (!saga) return <main className="page" aria-busy="true"><div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} /></main>;

  const shown = saga.parts.filter((p) => p.released || p.ratingKey);
  const upcoming = saga.parts.filter((p) => !p.released && !p.ratingKey);
  const missing = shown.filter((p) => !p.ratingKey);
  const pct = (n: number) => `${(n / Math.max(1, saga.total)) * 100}%`;
  const nextIdx = saga.next ? shown.findIndex((p) => p.tmdbId === saga.next!.tmdbId) + 1 : 0;

  const requestAll = async () => {
    setBulk('Aanvragen…');
    let ok = 0;
    for (const p of missing) {
      try {
        await seerrApi.request('movie', p.tmdbId, undefined, { title: p.title, poster: p.poster });
        ok++;
      } catch {
        // de rest proberen we wel
      }
    }
    setBulk(ok === missing.length ? `✓ ${ok} aangevraagd` : `${ok} van ${missing.length} aangevraagd`);
  };

  const ext = (p: SagaPart): ExtItem => ({ type: 'movie', tmdbId: p.tmdbId, title: p.title, year: p.year, overview: null, poster: p.poster, backdrop: p.backdrop, ratingKey: null });

  return (
    <main className="saga-page">
      <section className="hero hero-detail saga-hero" style={saga.backdrop ? { backgroundImage: `url("${saga.backdrop}")` } : undefined}>
        <div className="hero-body">
          <button className="btn btn-sm saga-back" onClick={goBack}>‹ Terug</button>
          <div className="eyebrow">Filmreeks · {saga.total} films</div>
          {saga.logo ? <img className="saga-logo" src={saga.logo} alt={saga.name} /> : <h1>{saga.name}</h1>}
          <div className="saga-stats">
            <span><strong className="ok">{saga.have} van {saga.total}</strong> in je bibliotheek</span>
            <span><strong>{saga.seen}</strong> gezien</span>
          </div>
          <span className="saga-bar big" aria-label={`${saga.seen} gezien, ${saga.have - saga.seen} nog niet gezien, ${saga.total - saga.have} ontbreekt`}>
            <span className="seen" style={{ width: pct(saga.seen) }} />
            <span className="have" style={{ width: pct(saga.have - saga.seen) }} />
          </span>
          {saga.overview && <p>{saga.overview}</p>}
          <div className="hero-actions" data-focus-group>
            {saga.next?.ratingKey && (
              <button className="btn btn-primary" data-autofocus onClick={() => navigate(`/play/${saga.next!.ratingKey}`)}>
                ▶ {saga.next.progress > 0 ? 'Verder' : 'Kijk'}: deel {nextIdx}
              </button>
            )}
            {missing.length > 0 && (
              <button className="btn btn-accent" disabled={!!bulk} onClick={() => void requestAll()}>
                {bulk ?? `Vraag ${missing.length} ontbrekende aan`}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="row" aria-label="Delen">
        <h2>Delen <span className="row-sub">Op volgorde van verschijnen</span></h2>
        <div className="row-track saga-parts" data-focus-group>
          {shown.map((p, i) => <PartCard key={p.tmdbId} p={p} n={i + 1} next={saga.next?.tmdbId === p.tmdbId} onRequest={() => setRequesting(ext(p))} />)}
        </div>
      </section>
      {upcoming.length > 0 && (
        <section className="row" aria-label="Binnenkort">
          <h2>Binnenkort</h2>
          <div className="row-track saga-parts" data-focus-group>
            {upcoming.map((p) => <PartCard key={p.tmdbId} p={p} n={0} next={false} onRequest={() => setRequesting(ext(p))} />)}
          </div>
        </section>
      )}
      <div className="footer-space" />
      {requesting && <RequestDialog item={requesting} onClose={() => { setRequesting(null); void load(); }} />}
    </main>
  );
}

function PartCard({ p, n, next, onRequest }: { p: SagaPart; n: number; next: boolean; onRequest: () => void }) {
  const tag = p.watched ? { text: '✓ Gezien', cls: 'seen' } : p.progress > 0 ? { text: `Verder: ${Math.round(p.progress * 100)}%`, cls: 'progress' } : p.ratingKey ? { text: 'In bibliotheek', cls: 'have' } : p.released ? { text: '+ Aanvragen', cls: 'missing' } : { text: p.year ? String(p.year) : 'Binnenkort', cls: 'missing' };
  const body = (
    <>
      <span className="part-art" style={p.poster ? { backgroundImage: `url("${p.poster}")` } : undefined}>
        {n > 0 && <span className="part-num">{n}</span>}
        <span className={`part-tag ${tag.cls}`}>{tag.text}</span>
        {p.progress > 0 && !p.watched && <span className="progress"><span style={{ width: `${Math.round(p.progress * 100)}%` }} /></span>}
      </span>
      <span className="card-title">{p.title}</span>
      <span className="card-sub">{p.year ?? ''}</span>
    </>
  );
  const cls = `card card-poster saga-part ${p.ratingKey ? '' : 'is-missing'} ${next ? 'is-next' : ''}`;
  return p.ratingKey ? <a className={cls} {...linkProps(`/item/${p.ratingKey}`)}>{body}</a> : <button className={cls} onClick={onRequest}>{body}</button>;
}
